/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 07/06/2025 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------
*/

import { createContext, useContext, useEffect, useState } from "react";
import { useLayout } from '../../context/System/LayoutContext'
import { useRequest } from "../System/RequestContext";
import { useSesion } from "../System/SesionContext";
import { useInventarios } from "../Inventarios/InventariosContext";
import { buildTicketVentaParts, layoutTieneObjetos } from "../../settings/ticketVentaEscape";
import { imprimirTicketEnPrinterTools } from "../../settings/ticketImpresion";

import {
    apiListaCajasRegistro,
    apiListaFiltroVentasCaja,
    apiMontoEnLetra,
    apiAplicarDevolucionCaja,
    apiObtenerRemisionVenta,
} from "../../settings/ApiConfig";

import { isErrorForm, isNullOrEmpty, handleValidarStrings, handleValidarTelefono, handleValidarCorreo, handleValidarNumerico, handleValidarHoras, getDateTimePost, getDatePost, getTimePost, handleValidarNumericoEntero } from "../../settings/utils";
import { defaultTypes } from "../../settings/enums";
import { useNavigate } from "react-router-dom";
import { HelpOutlineRounded, TrendingUpRounded } from "@mui/icons-material";
import { getResponseModel } from "../../settings/responseModel";
import { getPickersYearUtilityClass } from "@mui/x-date-pickers/YearCalendar/pickersYearClasses";

import { format } from "date-fns";
import { dateTimePickerTabsClasses } from "@mui/x-date-pickers";

const getVentaTotalesForm = () => ({
    subTotal: 0,
    totalDescuento: 0,
    totalIVA: 0,
    total: 0,
    tipoPago: "EFECTIVO",
    montoTipoPago: 0,
    montoEfectivo: 0,
    totalCambioDevueltoACliente: 0,
    montoEnLetra: "",
    serialTarjetaChequeVale: ""
});

export const ToolsContext = createContext();
export const ToolsProvider = (props) => {

    const { GetRequest, PostRequest } = useRequest();

    const { parametrosCajaForm, handleObtenerParametrosCajaSinValidación } = useSesion();
    const { handleSincronizarExistenciaArticulo } = useInventarios();

    const navigation = useNavigate();
    const { handleOpenAlert } = useLayout();
    const [ventaTotales, setVentaTotales] = useState(getVentaTotalesForm);

    const [statusCloseDevolucion, setStatusCloseDevolucion] = useState(false);
    const [statusWaiting, setStatusWaiting] = useState(false);

    const [caja, setCaja] = useState("");
    const [remision, setRemision] = useState(0);
    const [filtro, setFiltro] = useState("");
    const [fechaVenta, setFechaVenta] = useState(Date.now());
    const [listaRemisiones, setListaRemisiones] = useState([]);
    const [listaDevolucionesSelectas, setListaDevolucionesSelectas] = useState([]);

    const [listaCajas, setListaCajas] = useState([]);
    const [partidaSeleccionada, setPartidaSeleccionada] = useState(0);

    const [statusEnableFieldsDevolucion, setStatusEnableFieldsDevolucion] = useState(true);
    const [statusCanEditDevolucion, setStatusCanEditDevolucion] = useState(false);
    const [dirtyDevolucion, setDirtyDevolucion] = useState(false);

    const [originalListaDevoluciones, setOriginalListaDevoluciones] = useState([]);

    const handleConsultaMontoLetra = async (monto) => {
        // let MontoX = parseFloat(monto).toFixed(2);

        //setStatusWaiting(true);
        const response = await GetRequest({
            url: apiMontoEnLetra.replace("{monto}", monto),
        });
        //setStatusWaiting(false);

        if (response.hasError) {
            handleOpenAlert(response.message);
            return;
        }

        if (response.result.length === 0) {
            handleOpenAlert("No se realizó la operación de conversión de monto a letra");
            return;
        }

        return response.result;
    };

    const handleClickRegresarMainDashboard = () => {
        const route = "/*";
        navigation(route);
    };

    const handleCargarCajasSistema = async () => {
        const result = await GetRequest({
            url: apiListaCajasRegistro,
            loader: "Cargando cajas del sistema",
        });

        if (result.hasError) {
            handleOpenAlert('Error al obtener la lista de cajas del sistema');
            return;
        }

        const listaOriginal = await result?.result || [];

        const listaConIds = await listaOriginal.map((caja, index) => ({
            id: index + 1,
            caja: caja
        }));

        setListaCajas(listaConIds);
    };

    const handleChangeCaja = (e, newDat) => {
        setCaja(newDat.caja);
    };

    const handleAplicarFiltroCaja = async (e, data) => {
        setStatusWaiting(true);
        let name = e.target.name;
        let value = e.target.value;

        const hoy = new Date();
        hoy.setMonth(hoy.getMonth() - 1);

        const entity = {
            caja: name === "caja" ? value : caja ?? "",
            remision: name === "remision" ? value : remision == null ? 0 : remision,
            descripcion: name === "filtro" ? value : filtro.trim(),
            fechaventa: hoy,
        };

        await handleObtenerVentasPorFiltroCaja(entity);
        setStatusWaiting(false);
    };

    const handleObtenerVentasPorFiltroCaja = async (data) => {
        const data2 = {
            ...data,
            remision: data.remision === "" ? 0 : data.remision,
        };

        const result = await PostRequest({
            url: apiListaFiltroVentasCaja,
            body: data2,
            loader: "Cargando filtro de ventas por caja...",
        });

        if (result.hasError) {
            handleOpenAlert('No hay datos en lista de las ventas por caja: ' + result.message);
            setListaRemisiones([]);
            return;
        }

        const listaventas = await result?.result || [];
        const listaConIds = await listaventas.map((e, index) => ({
            ...e,  //los 3 ... son = Spread operator para heredar propiedades y datos
            id: index + 1,
            cantidaddevolucion: 0,
            conceptodevolucion: "",
            fechaventa: format(new Date(e.fechaventa), 'dd-MM-yyyy'),
            pcorigen: e.pc,
            almacenexhibidororigen: e.almacenexhibidor,
        }));

        setListaRemisiones(listaConIds);
    };

    const handleBuscarArticuloCodigoBarras = async (dataArticulo) => {

        if (!dataArticulo.clavearticulo) return;
        setFiltro(dataArticulo.descripcion.trim());

        const simulatedEvent = {
            target: {
                name: "filtro",
                value: dataArticulo.descripcion.trim(),
            }
        };

        await handleAplicarFiltroCaja(simulatedEvent, dataArticulo);
    };

    const handleRegistrarArticuloDevolucion = async (e, row) => {

        const exists = listaDevolucionesSelectas.some(item =>
            item.id === row.id
        );

        if (row.conceptodevolucion === null || row.empty) {
            handleOpenAlert("Falta asignar un concepto de devolución");
            return;
        }

        const cantidadOriginal = row.cantidadarticulo || 1;
        const cantidadDevuelta = row.cantidaddevolucion || 0;

        if (cantidadOriginal === 0 || cantidadDevuelta === 0) return;

        const proporcion = cantidadDevuelta / cantidadOriginal;

        // Crear una copia del artículo con los montos ajustados
        const remisionOrigen = row.remision;

        const articuloADevolver = {
            ...row,
            pc: parametrosCajaForm.caja,
            remision: parametrosCajaForm.folioventa,
            remisionorigen: remisionOrigen,
            descuentoarticulo: (row.descuentoarticulo || 0) * proporcion,
            subtotalarticulo: (row.subtotalarticulo || 0) * proporcion,
            ivaarticulo: (row.ivaarticulo || 0) * proporcion,
            totalarticulo: (row.totalarticulo || 0) * proporcion,
            idpartida: listaDevolucionesSelectas.length + 1,
        };

        if (originalListaDevoluciones.length === 0) {
            setOriginalListaDevoluciones([...listaDevolucionesSelectas]);
        }
        setListaDevolucionesSelectas(prev => [...prev, articuloADevolver]);
        setDirtyDevolucion(true);
    };

    const retotalizarCuentaDevolucion = async (dataVenta) => {
        if (!dataVenta || dataVenta.length === 0) {
            setVentaTotales({
                ...ventaTotales,
                totalDescuento: 0,
                subTotal: 0,
                totalIVA: 0,
                total: 0,
                montoEnLetra: "()",
            });
            return;
        }

        let totalDescuento = 0;
        let subTotal = 0;
        let totalIVA = 0;
        let total = 0;

        for (const item of dataVenta) {
            totalDescuento += (item.descuentoarticulo || 0);
            subTotal += (item.subtotalarticulo || 0);
            totalIVA += (item.ivaarticulo || 0);
            total += (item.totalarticulo || 0);
        }

        const montoEnLetra = await handleConsultaMontoLetra(total);

        setVentaTotales({
            ...ventaTotales,
            totalDescuento,
            subTotal,
            totalIVA,
            total,
            montoEnLetra,
        });
    };

    const handleEliminarDevolucion = async (dataDevolucion) => {
        if (originalListaDevoluciones.length === 0) {
            setOriginalListaDevoluciones([...listaDevolucionesSelectas]);
        }
        setListaDevolucionesSelectas([]);
        setDirtyDevolucion(true);
    };

    const handleEliminarPartidaDevolucion = (e, id) => {
        if (originalListaDevoluciones.length === 0) {
            setOriginalListaDevoluciones([...listaDevolucionesSelectas]);
        }
        const newList = listaDevolucionesSelectas.filter(a => a.id !== id);
        setListaDevolucionesSelectas(newList);
        setDirtyDevolucion(true);
        retotalizarCuentaDevolucion(newList);
    };

    const handleClickNuevaDevolucion = () => {
        setListaDevolucionesSelectas([]);
        setOriginalListaDevoluciones([]);
        setListaRemisiones([]);
        setCaja("");
        setFiltro("");
        setRemision(0);
        setStatusEnableFieldsDevolucion(false);
        setStatusCanEditDevolucion(true);
        setDirtyDevolucion(false);
        retotalizarCuentaDevolucion([]);
    };

    const handleDeshacerDevolucion = () => {
        setListaDevolucionesSelectas([...originalListaDevoluciones]);
        setDirtyDevolucion(false);
        retotalizarCuentaDevolucion([...originalListaDevoluciones]);
    };

    const handleCancelarDevolucion = () => {
        setListaDevolucionesSelectas([]);
        setOriginalListaDevoluciones([]);
        setListaRemisiones([]);
        setCaja("");
        setFiltro("");
        setRemision(0);
        setStatusEnableFieldsDevolucion(true);
        setStatusCanEditDevolucion(false);
        setDirtyDevolucion(false);
        retotalizarCuentaDevolucion([]);
    };

    const handleSincronizarDevoluciones = async () => {
        const data = listaDevolucionesSelectas;
        if (data.length === 0) {
            return;
        }

        setStatusWaiting(true);
        try {
            for (const item of data) {
                await handleSincronizarExistenciaArticulo(
                    item.clavearticulo || item.claveArticulo || item.clave,
                    item.medida,
                    item.almacenexhibidordestino
                );
            }
            handleOpenAlert("Sincronización de artículos terminada", "OK");
        } catch (error) {
            handleOpenAlert("Error al sincronizar artículos: " + error.message);
        } finally {
            setStatusWaiting(false);
        }
    };

    const handleApicarDevolucion = async (onSuccessCallback) => {
        if (parametrosCajaForm.caja === "CAJA-DEFAULT") {
            return;
        }

        const data = listaDevolucionesSelectas;
        if (data.length === 0) {
            return;
        }

        const result = await PostRequest({
            url: apiAplicarDevolucionCaja,
            body: data,
            loader: "Se está aplicando la devolución por caja..."
        });

        if (result.hasError) {
            handleOpenAlert("Error al aplicar devolución: " + result.message);
            return;
        }

        // Sincronizar artículos de la devolución
        for (const item of data) {
            await handleSincronizarExistenciaArticulo(
                item.clavearticulo || item.claveArticulo || item.clave,
                item.medida,
                item.almacenexhibidordestino
            );
        }

        //Se recibe el folio de la remisión en el result para impresión
        handleImprimirTicket(result.result.remision, result.result.caja.trim());
        handleClickNuevaDevolucion();

        // Ejecutar callback si existe
        if (onSuccessCallback && typeof onSuccessCallback === 'function') {
            await onSuccessCallback();
        }
    };

    const handleImprimirTicket = async (remision, caja) => {
        if (isNullOrEmpty(remision) || remision === 0 || isNullOrEmpty(caja)) return;

        let url = apiObtenerRemisionVenta.replace("{remision}", remision).replace("{caja}", caja);

        setStatusWaiting(true);
        const response = await GetRequest({
            url: url,
            loader: "Obteniendo remisión de venta en caja",
        });
        setStatusWaiting(false);

        if (response.hasError) {
            handleOpenAlert(response.message);
            return "";
        }

        if (response.result === undefined) return "";

        if (layoutTieneObjetos(parametrosCajaForm)) {
            const dataTicket = {
                encabezado: (parametrosCajaForm.encabezadoticket || "").trim(),
                cabecero: response.result.cabecero || {},
                detalles: (response.result.detalles || []).map((articulo) => ({
                    descripcion: articulo.descripcion || "",
                    cantidad: articulo.cantidad || 0,
                    medida: articulo.medida || 0,
                    costounitario: Number(articulo.costounitario) || 0,
                    descuento: Number(articulo.descuento) || 0,
                    total: Number(articulo.total) || 0,
                    vendedorpreventa: articulo.vendedorpreventa || "",
                    almacenexhibidor: articulo.almacenexhibidor || "",
                })),
            };
            const { partes } = buildTicketVentaParts({
                dataTicket,
                parametrosCajaForm,
                tipoDocumento: "VENTA",
                tituloDocumento: "COMPROBANTE DE DEVOLUCION",
                despedida: "Gracias por su compra!",
            });
            await imprimirTicketEnPrinterTools({
                partes,
                parametrosCajaForm,
                nombreImpresora: parametrosCajaForm.nombreimpresoraventas?.trim() || null,
            });
            handleOpenAlert("Impresion realizada", "OK");
            return "";
        }

        const formData = new FormData();
        const textEncoder = new TextEncoder();

        let ticketParts = [
            textEncoder.encode('\x1B\x40'),
        ];

        ticketParts.push(
            textEncoder.encode('\x1B\x61\x01\x1B\x21\x11' + 'COMPROBANTE DE DEVOLUCIÓN\n'),
            textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
            textEncoder.encode('Caja: ' + (response.result.cabecero.pc || "") + '\n'),
            textEncoder.encode('Remisión: ' + (response.result.cabecero.remision || "") + '\n'),
            textEncoder.encode('Vendedor: ' + (response.result.cabecero.usuario || "") + '\n'),
            textEncoder.encode('Fecha: ' + (response.result.cabecero.fechaventa || "") + '\n'),
            textEncoder.encode('Tipo de Pago: ' + (response.result.cabecero.tipopago || "") + '\n'),
            textEncoder.encode('Cliente: ' + (response.result.cabecero.razonsocial || "No especificado") + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n')
        );

        response.result.detalles.forEach((articulo) => {
            ticketParts.push(textEncoder.encode('Descripción: ' + (articulo.descripcion || "") + '\n'));
            ticketParts.push(textEncoder.encode('Cantidad: ' + (articulo.cantidad || 0) + ' - Talla: ' + (articulo.medida || 0) + '\n'));
            ticketParts.push(textEncoder.encode('P.Unit: $' + (articulo.costounitario || 0).toFixed(2) + '  Desc: $' + (articulo.descuento || 0).toFixed(2) + '  Importe: $' + (articulo.total || 0).toFixed(2) + '\n'));
        });

        ticketParts.push(
            textEncoder.encode('-'.repeat(32) + '\n'),
            textEncoder.encode('\x1B\x61\x00Subtotal: '),
            textEncoder.encode('\x1B\x61\x02$' + (response.result.cabecero.subtotal || 0).toFixed(2) + '\n'),
            textEncoder.encode('\x1B\x61\x00IVA: '),
            textEncoder.encode('\x1B\x61\x02$' + (response.result.cabecero.iva || 0).toFixed(2) + '\n'),
            textEncoder.encode('\x1B\x61\x00Total: '),
            textEncoder.encode('\x1B\x61\x02$' + (response.result.cabecero.total || 0).toFixed(2) + '\n')
        );

        ticketParts.push(
            textEncoder.encode('\x1B\x61\x00Efectivo: '),
            textEncoder.encode('\x1B\x61\x02$' + (response.result.cabecero.totalefectivo || 0).toFixed(2) + '\n'),
            textEncoder.encode('\x1B\x61\x00Cambio: '),
            textEncoder.encode('\x1B\x61\x02$' + (response.result.cabecero.totalcambio || 0).toFixed(2) + '\n'),
            textEncoder.encode('\x1B\x61\x01' + 'Gracias por su compra!\nVuelva pronto\n\n\n\n'),
            textEncoder.encode('\x1D\x56\x00')
        );

        const totalLength = ticketParts.reduce((sum, part) => sum + part.length, 0);
        const finalTicket = new Uint8Array(totalLength);
        let offset = 0;
        ticketParts.forEach(part => {
            finalTicket.set(part, offset);
            offset += part.length;
        });

        formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
        formData.append("printerName", parametrosCajaForm.nombreimpresoraventas.trim());

        const logoBase64 = parametrosCajaForm?.logo;
        if (logoBase64) {
            try {
                const binary = atob(logoBase64);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                formData.append("logo", new Blob([bytes], { type: "image/png" }), "logocliente.png");
            } catch (err) {
                handleOpenAlert("Logo no disponible:", err);
            }
        }

        try {
            let pc = parametrosCajaForm.caja.trim();
            const response2 = await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });

            if (!response2.ok) {
                handleOpenAlert(`Error en envío de impresión: ${response2.statusText}`);
                return;
            }

            handleOpenAlert("Impresión realizada", "OK");
        } catch (error) {
            handleOpenAlert(`Error en impresión: ${error.message}`);
        }
    };

    async function convertImageToBase64(imageSrc) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = imageSrc;
            img.crossOrigin = "Anonymous"; // Permite cargar imágenes de otros dominios

            img.onload = () => {
                const canvas = document.createElement("canvas");
                const cmToInches = 2.54;
                const dpi = 96; // Cambia este valor si tu impresora usa una resolución diferente
                const widthPx = 4 * dpi / cmToInches; // 4 cm a píxeles
                const heightPx = 4 * dpi / cmToInches; // 4 cm a píxeles

                canvas.width = widthPx;
                canvas.height = heightPx;

                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, widthPx, heightPx);

                const base64 = canvas.toDataURL("image/png").split(',')[1]; // Solo toma la parte de la base64
                resolve(base64);
            };

            img.onerror = (error) => {
                reject("Error al cargar la imagen: " + error);
            };
        });
    }

    return (
        <ToolsContext.Provider
            value={{
                statusCloseDevolucion,
                caja,
                setCaja,
                listaCajas,
                setListaCajas,
                remision,
                setRemision,
                fechaVenta,
                setFechaVenta,
                filtro,
                setFiltro,
                listaRemisiones,
                setListaRemisiones,
                handleChangeCaja,
                handleCargarCajasSistema,
                handleApicarDevolucion,
                handleSincronizarDevoluciones,
                handleClickRegresarMainDashboard,
                partidaSeleccionada,
                setPartidaSeleccionada,
                handleEliminarDevolucion,
                handleEliminarDevolucion,
                handleEliminarPartidaDevolucion,
                handleImprimirTicket,
                retotalizarCuentaDevolucion,
                handleObtenerVentasPorFiltroCaja,
                handleRegistrarArticuloDevolucion,
                handleAplicarFiltroCaja,
                statusWaiting, setStatusWaiting,
                handleBuscarArticuloCodigoBarras,
                listaDevolucionesSelectas, setListaDevolucionesSelectas,
                setVentaTotales, ventaTotales,
                partidaSeleccionada, setPartidaSeleccionada,
                handleClickNuevaDevolucion,
                handleDeshacerDevolucion,
                handleCancelarDevolucion,
                statusEnableFieldsDevolucion, setStatusEnableFieldsDevolucion,
                statusCanEditDevolucion, setStatusCanEditDevolucion,
                dirtyDevolucion, setDirtyDevolucion,
            }}
        >
            {props.children}
        </ToolsContext.Provider>
    );
};

export const useTools = () => useContext(ToolsContext);
